import React from 'react';
import './AuthPages.css';
import { useAuth } from '../../contexts/AuthContext';

const ProfileDashboardPage = () => {
  const { access, loading, user, isAuthenticated } = useAuth();

  const handleProfileUpdate = (event) => {
    event.preventDefault();
    // Placeholder for profile update submission logic.
  };

  const handleAddressUpdate = (event) => {
    event.preventDefault();
    // Placeholder for address update logic.
  };

  const handlePreferencesUpdate = (event) => {
    event.preventDefault();
    // Placeholder for preference update logic.
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-header">
          <span className="badge">Subscriber Hub</span>
          <h1>Loading your account</h1>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-header">
          <span className="badge">Subscriber Hub</span>
          <h1>Sign in required</h1>
          <p>You need an authenticated account session to view your profile and subscription access.</p>
        </div>
      </div>
    );
  }

  const subscriptionStatus = access?.subscription_status || 'inactive';
  const periodEnd = access?.current_period_end
    ? new Date(access.current_period_end).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'Not available';
  const accessLabel = access?.can_access_exclusive_content
    ? 'Active access'
    : 'No paid access';

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <span className="badge">Subscriber Hub</span>
        <h1>Your Profile &amp; Exclusive Access</h1>
        <p>
          Manage your subscription, track orders, and update your preferences to keep the
          experience tailored to you. Paid members unlock all exclusive content, curated
          deals, and member-only advertising opportunities.
        </p>
      </div>

      <div className="dashboard-grid">
        <section className="dashboard-card" aria-labelledby="subscription-status">
          <h2 id="subscription-status">Subscription Snapshot</h2>
          <p>
            Your current access state is <strong>{accessLabel}</strong>.
            Renewal / period end: <strong>{periodEnd}</strong>.
          </p>
          <ul>
            <li>Subscription status: <strong>{subscriptionStatus}</strong></li>
            <li>Plan code: <strong>{access?.plan_code || 'Not set'}</strong></li>
            <li>Exclusive content access: <strong>{access?.can_access_exclusive_content ? 'Enabled' : 'Locked'}</strong></li>
          </ul>
          <button className="secondary-button" type="button">
            Manage subscription
          </button>
        </section>

        <section className="dashboard-card" aria-labelledby="profile-updates">
          <h2 id="profile-updates">Profile &amp; Contact</h2>
          <form onSubmit={handleProfileUpdate} className="auth-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="profileEmail">Primary Email</label>
                <input id="profileEmail" name="profileEmail" type="email" defaultValue={user?.email || ''} />
              </div>
              <div className="form-group">
                <label htmlFor="backupEmail">Backup Email</label>
                <input id="backupEmail" name="backupEmail" type="email" placeholder="backup@example.com" />
              </div>
            </div>
            <button className="primary-button" type="submit">
              Save email settings
            </button>
          </form>
        </section>

        <section className="dashboard-card" aria-labelledby="address-management">
          <h2 id="address-management">Address Book</h2>
          <form onSubmit={handleAddressUpdate} className="auth-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="shippingAddress">Shipping Address</label>
                <textarea
                  id="shippingAddress"
                  name="shippingAddress"
                  rows="3"
                  defaultValue={`${user?.name || 'Subscriber'}\n`}
                />
              </div>
              <div className="form-group">
                <label htmlFor="billingAddress">Billing Address</label>
                <textarea
                  id="billingAddress"
                  name="billingAddress"
                  rows="3"
                  defaultValue={`${user?.name || 'Subscriber'}\n`}
                />
              </div>
            </div>
            <button className="primary-button" type="submit">
              Update addresses
            </button>
          </form>
        </section>

        <section className="dashboard-card" aria-labelledby="order-history">
          <h2 id="order-history">Recent Orders</h2>
          <ul>
            <li>
              <strong>Order #84392</strong> — Limited Edition Hoodie — <span>Shipped Dec 12</span>
            </li>
            <li>
              <strong>Order #83210</strong> — Signed Vinyl — <span>Delivered Nov 28</span>
            </li>
            <li>
              <strong>Order #82754</strong> — Fan Club Bundle — <span>Delivered Oct 19</span>
            </li>
          </ul>
          <button className="secondary-button" type="button">
            View full order history
          </button>
        </section>

        <section className="dashboard-card" aria-labelledby="preferences">
          <h2 id="preferences">Communication Preferences</h2>
          <form onSubmit={handlePreferencesUpdate} className="preference-list">
            <div className="checkbox-group">
              <input id="promoEmails" name="promoEmails" type="checkbox" />
              <label htmlFor="promoEmails">Stop receiving promotional emails</label>
            </div>
            <div className="checkbox-group">
              <input id="exclusiveEmails" name="exclusiveEmails" type="checkbox" defaultChecked />
              <label htmlFor="exclusiveEmails">Keep exclusive release alerts in my inbox</label>
            </div>
            <div className="checkbox-group">
              <input id="unsubscribe" name="unsubscribe" type="checkbox" />
              <label htmlFor="unsubscribe">
                Unsubscribe to free account (exclusive content will no longer be available until you re-subscribe)
              </label>
            </div>
            <button className="primary-button" type="submit">
              Save preferences
            </button>
          </form>
        </section>

        <section className="dashboard-card" aria-labelledby="deals">
          <h2 id="deals">Deals &amp; Sponsored Spots</h2>
          <p>
            Access curated offers from our partners and advertisers aligned with the GUHSO
            community. Deals refresh weekly and are exclusive to paying members.
          </p>
          <div className="deals-grid">
            <article className="deal-card">
              <h3>Studio Gear Upgrade</h3>
              <span>25% off mixers &amp; mics</span>
              <p>Powered by AudioVerse — premium setups for serious creators.</p>
            </article>
            <article className="deal-card">
              <h3>Travel &amp; Events</h3>
              <span>VIP lounge access</span>
              <p>FlyWithUs rewards for GUHSO members heading to live tapings.</p>
            </article>
            <article className="deal-card">
              <h3>Merch Collaborations</h3>
              <span>Early-bird drops</span>
              <p>Secure limited collaborations before they hit the public store.</p>
            </article>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProfileDashboardPage;
